/*
 * Turns a catalog fix-it link that points at the generic Integrations list
 * into a link to the integration the error is actually about ("Open Shopify"
 * → /home/integrations/shopify), using whatever text the error carries: the
 * provider's message, its code, the toast/notification title.
 *
 * Order matters: more specific names first (LinkedIn Ads before LinkedIn,
 * Meta Ads before Facebook, Google Calendar before Google Ads).
 */
const INTEGRATIONS: Array<{ test: RegExp; label: string; to: string }> = [
  { test: /shopify|myshopify|shpat_/i, label: "Shopify", to: "/home/integrations/shopify" },
  { test: /woo ?commerce|\bwc[-_ ]?api\b/i, label: "WooCommerce", to: "/home/integrations/woocommerce" },
  { test: /razorpay/i, label: "Razorpay", to: "/home/integrations/razorpay" },
  { test: /cashfree/i, label: "Cashfree", to: "/home/integrations/cashfree" },
  { test: /\bpayu\b/i, label: "PayU", to: "/home/integrations/payu" },
  { test: /petpooja/i, label: "Petpooja", to: "/home/integrations/petpooja" },
  { test: /linkedin[ _-]?ads/i, label: "LinkedIn Ads", to: "/home/integrations/linkedin-ads" },
  { test: /linkedin/i, label: "LinkedIn", to: "/home/integrations/linkedin" },
  { test: /tiktok[ _-]?ads/i, label: "TikTok Ads", to: "/home/integrations/tiktok-ads" },
  { test: /tiktok/i, label: "TikTok", to: "/home/integrations/tiktok" },
  { test: /youtube/i, label: "YouTube", to: "/home/integrations/youtube" },
  { test: /google[ _-]?calendar/i, label: "Google Calendar", to: "/home/integrations/google-calendar" },
  { test: /google[ _-]?ads/i, label: "Google Ads", to: "/home/integrations/google-ads" },
  { test: /\bzoom\b/i, label: "Zoom", to: "/home/integrations/zoom" },
  { test: /meta[ _-]?ads|ad ?account/i, label: "Meta Ads", to: "/home/integrations/meta-ads" },
  { test: /meta[ _-]?catalog|product catalog/i, label: "Meta Catalog", to: "/home/integrations/meta-catalog" },
  { test: /instagram/i, label: "Instagram", to: "/home/integrations/instagram" },
  { test: /facebook|\bfb\b/i, label: "Facebook", to: "/home/integrations/facebook" },
  { test: /whatsapp|\bwaba\b/i, label: "WhatsApp", to: "/home/integrations/whatsapp-business" },
  { test: /custom[ _-]?api/i, label: "Custom API", to: "/home/integrations/custom-api" },
];

const GENERIC_INTEGRATIONS_PATH = "/home/integrations";

type Action = { label: string; to: string } | undefined;

export function integrationLinkFor(context: string): { label: string; to: string } | null {
  if (!context) return null;
  const hit = INTEGRATIONS.find((i) => i.test.test(context));
  return hit ? { label: `Open ${hit.label}`, to: hit.to } : null;
}

/**
 * Points a generic "Open Integrations" link at the integration named in the
 * contexts, tried in order (the error's own text first, then weaker hints
 * such as a title or the current page).
 */
export function specificIntegrationAction(action: Action, ...contexts: Array<string | undefined>): Action {
  if (!action || action.to !== GENERIC_INTEGRATIONS_PATH) return action;
  for (const context of contexts) {
    const link = integrationLinkFor(context ?? "");
    if (link) return link;
  }
  return action;
}
